import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Typography,
  Stack,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Paper,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Popover,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Button,
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import UndoIcon from '@mui/icons-material/Undo';
import * as XLSX from 'xlsx';
import { useOutletContext, Link as RouterLink } from 'react-router-dom';
import api, { costsApi, exportsApi } from '../services/api';
import { mskToday } from '../utils/format';
import { useShell } from '../components/Layout';
import { EXPORT_LOCKED_TEXT, useExportAllowed } from '../components/common/ExportGate';
import { can } from '../utils/access';
import PeriodPicker from '../components/common/PeriodPicker';
import { presetPeriod, periodQuery } from '../utils/period';
import { tokens } from '../theme';
import { toneColor, tonePill } from '../utils/tone';
import { hasData } from '../utils/marketplaces';
import ComingSoon from '../components/common/ComingSoon';
import { groupByArticle, payoutByGroup, payoutKey, payoutWarnings } from '../utils/calculatorPayout';

// ─── Строки детализации из загруженных данных ─────────────────────────────────
// Report builder column → field of a realization row used by the calculator.
const REALIZATION_FIELDS = {
  nm_id: 'nm_id',
  vendor_code: 'sa_name',
  brand: 'brand_name',
  subject: 'subject_name',
  doc_type: 'doc_type_name',
  quantity: 'quantity',
  retail_amount: 'retail_amount',
  sales_commission: 'ppvz_sales_commission',
  acquiring_fee: 'acquiring_fee',
  delivery_service: 'delivery_rub',
  paid_storage: 'storage_fee',
  penalty: 'penalty',
  additional_payment: 'additional_payment',
  ppvz_reward: 'ppvz_reward',
  for_pay: 'ppvz_for_pay',
  deduction: 'deduction',
  paid_acceptance: 'acceptance',
  rebill_logistic: 'rebill_logistic_cost',
  cashback_amount: 'cashback_amount',
  cashback_discount: 'cashback_discount',
  cashback_commission_change: 'cashback_commission_change',
  payment_schedule: 'payment_schedule',
  installment_cofinancing: 'installment_cofinancing_amount',
};

const toCards = (result) => result.rows.map((row) => {
  const card = {};
  result.columns.forEach((c, i) => {
    const field = REALIZATION_FIELDS[c.key];
    if (field) card[field] = ['money', 'int', 'pct'].includes(c.kind) && row[i] != null ? Number(row[i]) : (row[i] ?? '');
  });
  return card;
});

// ─── Форматирование ───────────────────────────────────────────────────────────
const fmtRub = (v) =>
  new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 2,
  }).format(v || 0);

const fmtNum = (v) => new Intl.NumberFormat('ru-RU').format(v || 0);

// ─── Список переключаемых столбцов ───────────────────────────────────────────
const TOGGLE_COLS = [
  { id: 'sa_name',               label: 'Артикул продавца', group: 'Товар' },
  { id: 'nm_id',                 label: 'Артикул WB',       group: 'Товар' },
  { id: 'brand_name',            label: 'Бренд',            group: 'Товар' },
  { id: 'subject_name',          label: 'Предмет',          group: 'Товар' },
  { id: 'quantity',              label: 'Продаж',           group: 'Количество' },
  { id: 'return_qty',            label: 'Возвратов',        group: 'Количество' },
  { id: 'net_qty',               label: 'Чистые продажи',   group: 'Количество' },
  { id: 'retail_amount',         label: 'Оплатили покупатели', group: 'Финансы' },
  { id: 'ppvz_sales_commission', label: 'Комиссия WB',      group: 'Финансы' },
  { id: 'acquiring_fee',         label: 'Эквайринг',        group: 'Финансы' },
  { id: 'delivery_rub',          label: 'Логистика',        group: 'Финансы' },
  { id: 'storage_fee',           label: 'Хранение',         group: 'Финансы' },
  { id: 'penalty',               label: 'Штрафы',           group: 'Финансы' },
  { id: 'additional_payment',    label: 'Мин. платёж / корр. ВВ', group: 'Финансы' },
  { id: 'ppvz_reward',           label: 'Вознаграждение',   group: 'Финансы' },
  { id: 'ppvz_for_pay',          label: 'К перечислению',   group: 'Финансы' },
  { id: 'deduction',                    label: 'Удержания',          group: 'Финансы' },
  { id: 'acceptance',                   label: 'Приёмка',            group: 'Финансы' },
  { id: 'rebill_logistic_cost',         label: 'Возмещение логист.',  group: 'Финансы' },
  { id: 'cashback_amount',              label: 'Баллы лояльности',   group: 'Финансы' },
  { id: 'cashback_discount',            label: 'Компенс. скидки',    group: 'Финансы' },
  { id: 'cashback_commission_change',   label: 'Участие в лояльн.',  group: 'Финансы' },
  { id: 'payment_schedule',             label: 'Изм. срока оплаты',  group: 'Финансы' },
  { id: 'installment_cofinancing_amount', label: 'Скидка рассрочки', group: 'Финансы' },
  { id: 'effective_payout',             label: 'К выплате',          group: 'Финансы' },
  { id: 'cost_per_unit', label: 'Себест. (ед.)',  group: 'Чистая прибыль', netProfitOnly: true },
  { id: 'total_cost',    label: 'Затраты',         group: 'Чистая прибыль', netProfitOnly: true },
  { id: 'net_profit',    label: 'Чистая прибыль',  group: 'Чистая прибыль', netProfitOnly: true },
  { id: 'margin',        label: 'Маржа %',          group: 'Чистая прибыль', netProfitOnly: true },
];
const COLS_STORAGE_KEY = 'marqit_calc_hidden_cols';
const loadHiddenCols = () => {
  try {
    const saved = localStorage.getItem(COLS_STORAGE_KEY);
    if (saved) return new Set(JSON.parse(saved));
  } catch {}
  return new Set();
};

// ─── Поповер настройки столбцов ───────────────────────────────────────────────
const ColumnSettingsPopover = ({ anchor, onClose, hiddenCols, onChange, calcMode }) => {
  const groups = ['Товар', 'Количество', 'Финансы', 'Чистая прибыль'];
  return (
    <Popover
      open={Boolean(anchor)}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{ sx: { borderRadius: '16px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', p: 2, minWidth: 230 } }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: tokens.ink }}>Столбцы</Typography>
        <Button size="small" sx={{ fontSize: '0.75rem', color: tokens.primary, minWidth: 0 }} onClick={() => onChange(new Set())}>
          Показать все
        </Button>
      </Box>
      {groups.map((group) => {
        const cols = TOGGLE_COLS.filter((c) => c.group === group && (!c.netProfitOnly || calcMode === 'net_profit'));
        if (!cols.length) return null;
        return (
          <Box key={group} mb={1}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: tokens.inkMuted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>
              {group}
            </Typography>
            <FormGroup>
              {cols.map((col) => (
                <FormControlLabel
                  key={col.id}
                  sx={{ ml: 0, mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={!hiddenCols.has(col.id)}
                      onChange={(e) => {
                        const next = new Set(hiddenCols);
                        if (e.target.checked) next.delete(col.id); else next.add(col.id);
                        onChange(next);
                      }}
                      sx={{ '&.Mui-checked': { color: tokens.primary }, py: 0.25 }}
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.85rem', color: tokens.ink }}>{col.label}</Typography>}
                />
              ))}
            </FormGroup>
          </Box>
        );
      })}
    </Popover>
  );
};

// ─── Статьи расходов ─────────────────────────────────────────────────────────
const EXPENSE_CATEGORIES = [
  { key: 'delivery_rub',                   label: 'Логистика' },
  { key: 'storage_fee',                    label: 'Хранение' },
  { key: 'penalty',                        label: 'Штрафы' },
  { key: 'deduction',                      label: 'Удержания' },
  { key: 'acceptance',                     label: 'Приёмка' },
  { key: 'additional_payment',             label: 'Мин. платёж / корр. ВВ' },
  { key: 'cashback_amount',                label: 'Баллы лояльности' },
  { key: 'cashback_commission_change',     label: 'Участие в лояльности' },
  { key: 'payment_schedule',              label: 'Изм. срока оплаты' },
  { key: 'installment_cofinancing_amount', label: 'Скидка рассрочки' },
];
const rowTotalExpenses = (row) =>
  EXPENSE_CATEGORIES.reduce((sum, cat) => sum + (row[cat.key] || 0), 0);

// ─── SummaryCard ──────────────────────────────────────────────────────────────
const SummaryCard = ({ icon, label, value, valueColor = tokens.ink, color = tokens.primary, bg = 'rgba(25,198,192,0.1)', onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      background: tokens.paper,
      borderRadius: `${tokens.radius}px`,
      border: `1px solid ${tokens.border}`,
      boxShadow: tokens.shadowCard,
      p: 2.5,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      ...(onClick && {
        cursor: 'pointer',
        transition: 'box-shadow 0.15s, transform 0.15s',
        '&:hover': {
          boxShadow: tokens.shadowRaised,
          transform: 'translateY(-2px)',
        },
      }),
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: '12px',
        background: bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        flexShrink: 0,
        '& svg': { fontSize: '1.375rem' },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ whiteSpace: 'nowrap' }}>
      <Typography sx={{ fontSize: '1.375rem', fontWeight: 800, color: valueColor, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: tokens.inkSoft, fontWeight: 500, mt: 0.3 }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

// ─── Справка по полям ─────────────────────────────────────────────────────────
const FIELD_MAP = [
  {
    group: 'Товар',
    rows: [
      { calc: 'Артикул продавца', xlsx: 'Артикул поставщика', field: 'sa_name', note: '' },
      { calc: 'Артикул WB',       xlsx: 'Код номенклатуры',   field: 'nm_id',   note: '' },
      { calc: 'Бренд',            xlsx: 'Бренд',              field: 'brand_name', note: '' },
      { calc: 'Предмет',          xlsx: 'Предмет',            field: 'subject_name', note: '' },
    ],
  },
  {
    group: 'Количество',
    rows: [
      { calc: 'Продаж (шт.)',        xlsx: 'Кол-во',  field: 'quantity',   note: 'Только строки с типом «Продажа»' },
      { calc: 'Возвратов (шт.)',     xlsx: 'Кол-во',  field: 'return_qty', note: 'Только строки с типом «Возврат»' },
      { calc: 'Чистые продажи',      xlsx: '—',       field: '',           note: 'Расчётное: Продаж − Возвратов' },
    ],
  },
  {
    group: 'Финансы (из отчёта)',
    rows: [
      { calc: 'Оплатили покупатели', xlsx: 'Вайлдберриз реализовал Товар (Пр)',           field: 'retail_amount',        note: 'Сколько заплатили покупатели (после скидок WB), продажи минус возвраты. Это не «Выручка» с дашборда: выручка считается по цене продавца' },
      { calc: 'Комиссия WB',     xlsx: 'Услуги по реализации',                           field: 'ppvz_sales_commission', note: 'Уже вычтена WB из «К перечислению»' },
      { calc: 'Эквайринг',       xlsx: 'Эквайринг',                                      field: 'acquiring_fee',        note: 'Уже вычтена WB из «К перечислению»' },
      { calc: 'Логистика',       xlsx: 'Услуги по доставке товара покупателю',           field: 'delivery_rub',         note: 'Вычитается при расчёте «К выплате»' },
      { calc: 'Хранение',        xlsx: 'Хранение',                                       field: 'storage_fee',          note: 'Вычитается при расчёте «К выплате»' },
      { calc: 'Штрафы',          xlsx: 'Штрафы',                                         field: 'penalty',              note: 'Вычитается при расчёте «К выплате»' },
      { calc: 'Мин. платёж / корр. ВВ', xlsx: 'Корректировка Вознаграждения ВВ',               field: 'additional_payment',           note: 'Удержание: вычитается из «К выплате». Сюда входит остаток по минимальному платежу. Минус в отчёте = доплата вам' },
      { calc: 'Вознаграждение ПВЗ', xlsx: 'Возмещение за выдачу и возврат товаров на ПВЗ', field: 'ppvz_reward',          note: 'Справочно, не вычитается: уже внутри комиссии WB в «К перечислению»' },
      { calc: 'К перечислению',  xlsx: 'К перечислению Продавцу за реализованный Товар', field: 'ppvz_for_pay',         note: 'База. Комиссия WB и эквайринг уже внутри' },
      { calc: 'Удержания',       xlsx: 'Удержание',                                      field: 'deduction',            note: 'Вычитается при расчёте «К выплате»' },
      { calc: 'Приёмка',         xlsx: 'Операции при приёмке',                           field: 'acceptance',           note: 'Вычитается при расчёте «К выплате»' },
      { calc: 'Возмещение логист.', xlsx: 'Возмещение издержек по перевозке',            field: 'rebill_logistic_cost', note: 'Справочно, не вычитается: по данным WB не влияет на доход' },
      { calc: 'Баллы лояльности',   xlsx: 'Баллы программы лояльности (удержание)',      field: 'cashback_amount',           note: 'Вычитается с учётом знака из отчёта' },
      { calc: 'Компенс. скидки',    xlsx: 'Компенсация скидки по программе лояльности',  field: 'cashback_discount',         note: 'Прибавляется к «К выплате» один раз: это начисление от WB, с сохранением знака суммы' },
      { calc: 'Участие в лояльн.',  xlsx: 'Стоимость участия в программе лояльности',    field: 'cashback_commission_change', note: 'Вычитается с учётом знака из отчёта' },
      { calc: 'Изм. срока оплаты',  xlsx: 'Разовое изменение срока перечисления',        field: 'payment_schedule',          note: 'Вычитается с учётом знака из отчёта' },
      { calc: 'Скидка рассрочки',   xlsx: 'Скидка по программе софинансирования',        field: 'installment_cofinancing_amount', note: 'Справочно, не вычитается: уже внутри «К перечислению»' },
    ],
  },
  {
    group: 'Расчётные поля (в отчёте xlsx отсутствуют)',
    rows: [
      { calc: 'К выплате', xlsx: '—', field: '', note: 'Считается на сервере так же, как «К перечислению» в сводке и мастер-отчёте, по тем же строкам отчётов (только в рублях): К перечислению + компенсация скидки лояльности − логистика − хранение − штрафы − удержания − приёмка − мин. платёж / корр. ВВ − баллы и участие в лояльности − изм. срока оплаты. Комиссия WB и эквайринг уже внутри «К перечислению»; ПВЗ, возмещение перевозки и скидка рассрочки справочно' },
      { calc: 'Чистая прибыль', xlsx: '—', field: '', note: 'К выплате − Себестоимость × Чистые продажи' },
      { calc: 'Маржа %', xlsx: '—', field: '', note: 'Чистая прибыль / Оплатили покупатели × 100%' },
    ],
  },
];

const HelpDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper">
    <DialogTitle
      sx={{
        fontWeight: 800,
        fontSize: '1.1rem',
        color: tokens.ink,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pb: 1,
        whiteSpace: 'nowrap',
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <HelpOutlineIcon sx={{ color: tokens.primary }} />
        Справка: поля калькулятора → колонки в отчёте WB (xlsx)
      </Box>
      <IconButton size="small" onClick={onClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers sx={{ p: 0 }}>
      {FIELD_MAP.map((section) => (
        <Box key={section.group}>
          <Box
            sx={{
              px: 2.5,
              py: 1,
              background: 'rgba(25,198,192,0.06)',
              borderBottom: '1px solid rgba(25,198,192,0.12)',
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: tokens.primary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {section.group}
            </Typography>
          </Box>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '33.33%' }} />
              <col style={{ width: '33.33%' }} />
              <col style={{ width: '33.33%' }} />
            </colgroup>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.78rem', color: tokens.inkSoft, background: '#fafafa', whiteSpace: 'nowrap' } }}>
                <TableCell>Поле в калькуляторе</TableCell>
                <TableCell>Колонка в отчёте xlsx</TableCell>
                <TableCell>Комментарий</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {section.rows.map((r) => (
                <TableRow key={r.calc} hover>
                  <TableCell sx={{ fontWeight: 600, color: tokens.ink, whiteSpace: 'nowrap' }}>
                    {r.calc}
                  </TableCell>
                  <TableCell sx={{ color: r.xlsx === '—' ? tokens.inkMuted : tokens.ink, fontStyle: r.xlsx === '—' ? 'italic' : 'normal' }}>
                    {r.xlsx}
                    {r.field && (
                      <Typography component="span" sx={{ ml: 1, fontSize: '0.72rem', color: tokens.inkMuted, fontFamily: 'monospace' }}>
                        ({r.field})
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: tokens.inkSoft }}>{r.note || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ))}
    </DialogContent>
  </Dialog>
);

// ─── CalculatorPage ───────────────────────────────────────────────────────────
const Calculator = ({ marketplace }) => {
  const { profile } = useShell();
  const canEditCosts = can(profile, 'costs', 'edit');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [period, setPeriod] = useState(() => presetPeriod(30));
  const [calcMode, setCalcMode] = useState(() => localStorage.getItem('marqit_calc_mode') || 'by_article');
  const [reportData, setReportData] = useState(null);
  const [costPriceList, setCostPriceList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('ppvz_for_pay');
  const [sortOrder, setSortOrder] = useState('desc');
  const [helpOpen, setHelpOpen] = useState(false);
  const [hiddenCols, setHiddenCols] = useState(loadHiddenCols);
  const [colsAnchor, setColsAnchor] = useState(null);
  const [detailCard, setDetailCard] = useState(null); // 'articles'|'net_qty'|'retail_amount'|'effective_payout'|'expenses'

  // Inline cost editing
  const [inlineCostId, setInlineCostId] = useState(null); // nm_id
  const [inlineCostValue, setInlineCostValue] = useState('');
  const costOriginalRef = useRef(new Map()); // nm_id → original cost_price from server
  const [editedCostIds, setEditedCostIds] = useState(new Set()); // nm_ids edited this session

  const handleCostEditStart = (row) => {
    if (!canEditCosts) return;
    setInlineCostId(row.nm_id);
    setInlineCostValue(String(row.has_cost ? row.cost_per_unit : ''));
  };

  // Costs live in one history shared with the «Себестоимость» page and analytics: a value set here
  // applies from today (the page «Себестоимость» can date it back).
  const reloadCosts = () => costsApi.list(selectedAccountId)
    .then((r) => setCostPriceList((r.data || []).filter((c) => c.cost_price != null)))
    .catch(() => {});

  const handleCostEditSave = async (row) => {
    const val = parseFloat(inlineCostValue);
    setInlineCostId(null);
    if (isNaN(val) || val < 0) return;
    const nmIdStr = String(row.nm_id);
    // Mark as manually edited (only if value actually changed from original)
    const original = costOriginalRef.current.get(nmIdStr);
    if (original === undefined || original !== val) {
      setEditedCostIds((prev) => new Set([...prev, nmIdStr]));
    }
    setCostPriceList((prev) => [...prev.filter((c) => String(c.nm_id) !== nmIdStr), { nm_id: row.nm_id, cost_price: val }]);
    try {
      await costsApi.set(selectedAccountId, mskToday(), [{ nm_id: row.nm_id, cost_price: val.toFixed(2) }]);
    } catch {
      reloadCosts();
    }
  };

  const handleCostReset = async (e, row) => {
    e.stopPropagation();
    const nmIdStr = String(row.nm_id);
    const original = costOriginalRef.current.get(nmIdStr);
    setEditedCostIds((prev) => { const next = new Set(prev); next.delete(nmIdStr); return next; });
    try {
      // drop today's entry made here; the value in force before it comes back
      const { data: history } = await costsApi.history(selectedAccountId, row.nm_id);
      const todays = (history || []).find((h) => h.valid_from === mskToday());
      if (todays && original === undefined) await costsApi.deletePeriod(todays.id);
      else if (original !== undefined) await costsApi.set(selectedAccountId, mskToday(), [{ nm_id: row.nm_id, cost_price: Number(original).toFixed(2) }]);
    } catch {
      // the list below shows what is stored
    }
    reloadCosts();
  };

  const handleCostEditKeyDown = (e, row) => {
    if (e.key === 'Enter') handleCostEditSave(row);
    if (e.key === 'Escape') setInlineCostId(null);
  };

  const handleHiddenColsChange = (next) => {
    setHiddenCols(next);
    try { localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify([...next])); } catch {}
  };

  // h(fieldId) → sx для скрытия ячейки
  const h = (fieldId) => hiddenCols.has(fieldId) ? { display: 'none' } : {};

  const exportAllowed = useExportAllowed();
  const downloadXLSX = () => {
    const cols = TOGGLE_COLS.filter((c) => !hiddenCols.has(c.id) && (!c.netProfitOnly || calcMode === 'net_profit'));
    const getValue = (colId, row) => {
      const v = row[colId];
      if (v == null) return '';
      return typeof v === 'number' ? v : String(v);
    };
    const header = cols.map((c) => c.label);
    const dataRows = rows.map((row) => cols.map((c) => getValue(c.id, row)));
    const ws = XLSX.utils.aoa_to_sheet([header, ...dataRows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Калькулятор');
    XLSX.writeFile(wb, `calculator_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Загрузка аккаунтов
  useEffect(() => {
    api
      .get('/marketplace/accounts')
      .then((r) => {
        const list = (r.data || []).filter((a) => a.marketplace === marketplace);
        setAccounts(list);
        if (list.length > 0) {
          const saved = localStorage.getItem('marqit_selected_account_id');
          const found = saved && list.find((a) => a.id === saved);
          setSelectedAccountId(found ? saved : list[0].id);
        }
      })
      .catch(() => {});
  }, [marketplace]);

  // Строки детализации реализации за период — из данных, которые MARQIT уже загрузил из кабинета
  useEffect(() => {
    if (!selectedAccountId) {
      setReportData(null);
      return;
    }
    let live = true;
    setLoading(true);
    setError('');
    setReportData(null);
    exportsApi
      .build({ type: 'realization', purpose: 'calculator', account_id: selectedAccountId, columns: Object.keys(REALIZATION_FIELDS).join(','), ...periodQuery(period) })
      .then((r) => {
        if (!live) return;
        // partial rows would give wrong totals: show the reason instead of them
        if (r.data.truncated) {
          setError('За этот период слишком много строк для расчёта — выберите период короче.');
          return;
        }
        setReportData({ raw_data: { cards: toCards(r.data) }, coverage: r.data.coverage?.[0], payout: r.data.payout });
      })
      .catch((e) => { if (live) setError(e.response?.data?.error || 'Ошибка загрузки данных'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [selectedAccountId, period]);

  // Загрузка себестоимостей при режиме "Чистая прибыль"
  useEffect(() => {
    if (!selectedAccountId || calcMode !== 'net_profit') {
      setCostPriceList([]);
      costOriginalRef.current = new Map();
      setEditedCostIds(new Set());
      return;
    }
    costsApi
      .list(selectedAccountId)
      .then((r) => {
        const list = (r.data || []).filter((c) => c.cost_price != null);
        setCostPriceList(list);
        costOriginalRef.current = new Map(list.map((c) => [String(c.nm_id), Number(c.cost_price)]));
        setEditedCostIds(new Set());
      })
      .catch(() => setCostPriceList([]));
  }, [selectedAccountId, calcMode]);

  // Группировка по артикулу
  const grouped = useMemo(() => {
    if (!reportData?.raw_data?.cards) return [];
    return groupByArticle(reportData.raw_data.cards);
  }, [reportData]);

  // Карта себестоимостей: nm_id → cost_price
  const costMap = useMemo(() => {
    const map = new Map();
    for (const cp of costPriceList) {
      map.set(String(cp.nm_id), Number(cp.cost_price || 0));
    }
    return map;
  }, [costPriceList]);

  // К выплате по строкам — с сервера (тот же расчёт, что в сводке и мастер-отчёте)
  const payoutMap = useMemo(() => payoutByGroup(reportData?.payout), [reportData]);
  const payoutNotes = useMemo(() => (reportData ? payoutWarnings(reportData.payout, fmtRub) : []), [reportData]);

  // Финальные строки с расчётами
  const rows = useMemo(() => {
    let data = grouped.map((row) => {
      // net units can be negative when returns exceed sales: their cost comes back
      const netQty = (row.quantity || 0) - (row.return_qty || 0);

      // К выплате: с сервера; null — у строки нет выплаты (другая валюта или вне отчётов)
      const key = payoutKey(row.nm_id, row.sa_name);
      const effectivePayout = payoutMap.has(key) ? payoutMap.get(key) : null;

      if (calcMode === 'net_profit') {
        // a cost price of 0 is a value; only a missing one is «не задана»
        const hasCost = costMap.has(String(row.nm_id));
        const costPerUnit = hasCost ? costMap.get(String(row.nm_id)) : 0;
        // Сервисная строка: расходы WB без привязки к товару (хранение, удержания, реклама)
        const isServiceRow = !row.nm_id && !row.sa_name;
        const totalCost = costPerUnit * netQty;
        const netProfit = effectivePayout == null ? null
                        : hasCost ? effectivePayout - totalCost
                        : isServiceRow ? effectivePayout
                        : null;
        const margin =
          hasCost && netProfit != null && row.retail_amount && row.retail_amount !== 0
            ? (netProfit / row.retail_amount) * 100
            : null;
        return {
          ...row,
          net_qty: netQty,
          effective_payout: effectivePayout,
          cost_per_unit: costPerUnit,
          has_cost: hasCost,
          is_service_row: isServiceRow,
          total_cost: totalCost,
          net_profit: netProfit,
          margin,
        };
      }
      return { ...row, net_qty: netQty, effective_payout: effectivePayout };
    });

    // Фильтр по поиску
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (r) =>
          String(r.nm_id || '').includes(q) ||
          String(r.sa_name || '').toLowerCase().includes(q) ||
          String(r.brand_name || '').toLowerCase().includes(q) ||
          String(r.subject_name || '').toLowerCase().includes(q)
      );
    }

    // Сортировка
    if (sortField) {
      data.sort((a, b) => {
        const av = a[sortField];
        const bv = b[sortField];
        if (typeof av === 'number' && typeof bv === 'number') {
          return sortOrder === 'asc' ? av - bv : bv - av;
        }
        return sortOrder === 'asc'
          ? String(av || '').localeCompare(String(bv || ''))
          : String(bv || '').localeCompare(String(av || ''));
      });
    }

    return data;
  }, [grouped, payoutMap, calcMode, costMap, search, sortField, sortOrder]);

  // Итоговая строка
  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          quantity: acc.quantity + (r.quantity || 0),
          return_qty: acc.return_qty + (r.return_qty || 0),
          net_qty: acc.net_qty + (r.net_qty || 0),
          retail_amount: acc.retail_amount + (r.retail_amount || 0),
          ppvz_reward: acc.ppvz_reward + (r.ppvz_reward || 0),
          delivery_rub: acc.delivery_rub + (r.delivery_rub || 0),
          rebill_logistic_cost: acc.rebill_logistic_cost + (r.rebill_logistic_cost || 0),
          storage_fee: acc.storage_fee + (r.storage_fee || 0),
          deduction: acc.deduction + (r.deduction || 0),
          acceptance: acc.acceptance + (r.acceptance || 0),
          penalty: acc.penalty + (r.penalty || 0),
          additional_payment: acc.additional_payment + (r.additional_payment || 0),
          cashback_amount: acc.cashback_amount + (r.cashback_amount || 0),
          cashback_discount: acc.cashback_discount + (r.cashback_discount || 0),
          cashback_commission_change: acc.cashback_commission_change + (r.cashback_commission_change || 0),
          payment_schedule: acc.payment_schedule + (r.payment_schedule || 0),
          installment_cofinancing_amount: acc.installment_cofinancing_amount + (r.installment_cofinancing_amount || 0),
          ppvz_for_pay: acc.ppvz_for_pay + (r.ppvz_for_pay || 0),
          effective_payout: acc.effective_payout + (r.effective_payout || 0),
          total_cost: acc.total_cost + (r.total_cost || 0),
          // net_profit: товарные строки с себестоимостью + сервисные строки (хранение, удержания и т.п.)
          net_profit: acc.net_profit + ((r.has_cost || r.is_service_row) && r.net_profit !== null ? r.net_profit : 0),
        }),
        {
          quantity: 0,
          return_qty: 0,
          net_qty: 0,
          retail_amount: 0,
          ppvz_reward: 0,
          delivery_rub: 0,
          rebill_logistic_cost: 0,
          storage_fee: 0,
          deduction: 0,
          acceptance: 0,
          penalty: 0,
          additional_payment: 0,
          cashback_amount: 0,
          cashback_discount: 0,
          cashback_commission_change: 0,
          payment_schedule: 0,
          installment_cofinancing_amount: 0,
          ppvz_for_pay: 0,
          effective_payout: 0,
          total_cost: 0,
          net_profit: 0,
        }
      ),
    [rows]
  );

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const SortCell = ({ field, label, align = 'right' }) => (
    <TableCell align={align} sx={{ whiteSpace: 'nowrap', fontWeight: 700, ...h(field) }}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortOrder : 'desc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  // Количество артикулов без себестоимости (для предупреждения)
  const missingCostCount = useMemo(() => {
    if (calcMode !== 'net_profit') return 0;
    return rows.filter((r) => !costMap.has(String(r.nm_id))).length;
  }, [rows, calcMode, costMap]);

  return (
    <Box>
      <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
      <ColumnSettingsPopover
        anchor={colsAnchor}
        onClose={() => setColsAnchor(null)}
        hiddenCols={hiddenCols}
        onChange={handleHiddenColsChange}
        calcMode={calcMode}
      />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 2.5 }}>
        <Box>
          <Typography variant="h3">Калькулятор прибыли</Typography>
          <Typography variant="body2" color="text.secondary">Разбор финансовых отчётов WB за выбранный период по каждому артикулу: продажи, расходы, выплата и чистая прибыль.</Typography>
        </Box>
        <Button variant="outlined" startIcon={<HelpOutlineIcon />} onClick={() => setHelpOpen(true)}>Откуда берутся цифры</Button>
      </Stack>

      {/* ── Панель настроек ── */}
      <Box
        sx={{
          background: tokens.paper,
          borderRadius: `${tokens.radius}px`,
          border: `1px solid ${tokens.border}`,
          boxShadow: tokens.shadowCard,
          p: 3,
          mb: 3,
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'flex-start' }}
          sx={{ mb: 3 }}
        >
          <Typography variant="h3" sx={{ pt: { lg: 1.25 } }}>
            Параметры расчёта
          </Typography>
          {/* Период — отдельной строкой сверху, чтобы не теснить кабинет и тип расчёта */}
          <Box sx={{ minWidth: 0 }}>
            <PeriodPicker value={period} onChange={(p) => { setPeriod(p); setSearch(''); }} />
          </Box>
        </Stack>
        <Grid container spacing={2} alignItems="flex-end">
          {/* Аккаунт */}
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Кабинет</InputLabel>
              <Select
                value={selectedAccountId}
                label="Кабинет"
                onChange={(e) => {
                  setSelectedAccountId(e.target.value);
                  localStorage.setItem('marqit_selected_account_id', e.target.value);
                  setReportData(null);
                  setSearch('');
                }}
              >
                {accounts.length === 0 ? (
                  <MenuItem value="" disabled>
                    Нет аккаунтов
                  </MenuItem>
                ) : (
                  accounts.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name || a.marketplace}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>

          {/* Режим расчёта */}
          <Grid item xs={12} sm={6} md={8}>
            <Box>
              <Typography sx={{ fontSize: '0.75rem', color: tokens.inkSoft, mb: 0.75, fontWeight: 600 }}>
                Тип расчёта
              </Typography>
              <ToggleButtonGroup
                value={calcMode}
                exclusive
                onChange={(_, val) => { if (val) { setCalcMode(val); localStorage.setItem('marqit_calc_mode', val); } }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    borderRadius: '10px !important',
                    border: '1px solid rgba(25,198,192,0.25) !important',
                    px: 2,
                    py: 0.75,
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: tokens.inkSoft,
                    '&.Mui-selected': {
                      background: tokens.primary,
                      color: '#fff',
                    },
                  },
                  gap: 1,
                }}
              >
                <ToggleButton value="by_article">
                  <InventoryIcon sx={{ fontSize: '1rem', mr: 0.75 }} />
                  По артикулам
                </ToggleButton>
                <ToggleButton value="net_profit">
                  <AttachMoneyIcon sx={{ fontSize: '1rem', mr: 0.75 }} />
                  Чистая прибыль
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
        </Grid>

        {/* Пояснение режима */}
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: '10px',
            background: calcMode === 'net_profit' ? 'rgba(191,243,227,0.3)' : 'rgba(25,198,192,0.05)',
            border: `1px solid ${calcMode === 'net_profit' ? 'rgba(0,168,120,0.2)' : 'rgba(25,198,192,0.12)'}`,
          }}
        >
          {calcMode === 'by_article' ? (
            <Typography sx={{ fontSize: '0.8rem', color: tokens.inkSoft }}>
              <strong style={{ color: tokens.primary }}>По артикулам</strong> — суммирование всех строк отчёта
              по каждому артикулу: продажи, возвраты, логистика, хранение, штрафы и итоговая выплата WB.
            </Typography>
          ) : (
            <Typography sx={{ fontSize: '0.8rem', color: tokens.ink }}>
              <strong style={{ color: tokens.success }}>Чистая прибыль</strong> — формула:{' '}
              <strong>К выплате − Себестоимость × Чистые продажи</strong>.{' '}
              Где <strong>К выплате</strong> = К перечислению − Логистика − Хранение − Штрафы − Удержания − Приёмка.
              Комиссия WB и эквайринг в «К выплате» уже учтены — они вычтены WB при начислении «К перечислению».
              Артикулы без себестоимости выделены красным и не учитываются в итоге чистой прибыли.
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── Состояния загрузки / ошибки ── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress sx={{ color: tokens.primary }} />
        </Box>
      )}

      {/* ── Результаты ── */}
      {!loading && rows.length > 0 && (
        <>
          {/* Предупреждение об отсутствующей себестоимости */}
          {calcMode === 'net_profit' && missingCostCount > 0 && (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{ mb: 2 }}
            >
              {missingCostCount} из {rows.length} артикулов не имеют себестоимости — они выделены красным и
              не учитываются в итоге чистой прибыли. Добавьте их в разделе «Себестоимость и налоги».
            </Alert>
          )}

          {/* Почему сумма к выплате предварительная */}
          {payoutNotes.length > 0 && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
              Сумма «К выплате» предварительная:
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {payoutNotes.map((n) => <li key={n}>{n}</li>)}
              </Box>
            </Alert>
          )}

          {/* Итоговые карточки */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              mb: 3,
              alignItems: 'stretch',
            }}
          >
            <SummaryCard
              icon={<InventoryIcon />}
              label="Артикулов"
              value={fmtNum(rows.length)}
              color={tokens.primary}
              bg="rgba(25,198,192,0.1)"
              onClick={() => setDetailCard('articles')}
            />
            <SummaryCard
              icon={<TrendingUpIcon />}
              label="Чистые продажи (шт.)"
              value={fmtNum(totals.net_qty)}
              color={tokens.success}
              bg="rgba(191,243,227,0.5)"
              onClick={() => setDetailCard('net_qty')}
            />
            <SummaryCard
              icon={<AttachMoneyIcon />}
              label="Оплатили покупатели"
              value={fmtRub(totals.retail_amount)}
              color={tokens.primary}
              bg="rgba(25,198,192,0.08)"
              onClick={() => setDetailCard('retail_amount')}
            />
            <SummaryCard
              icon={<AttachMoneyIcon />}
              label={reportData?.payout?.final ? 'К выплате' : 'К выплате (предварительно)'}
              value={reportData?.payout ? fmtRub(totals.effective_payout) : '—'}
              valueColor={reportData?.payout ? toneColor(totals.effective_payout) : undefined}
              color={tokens.success}
              bg="rgba(191,243,227,0.4)"
              onClick={() => setDetailCard('effective_payout')}
            />
            <SummaryCard
              icon={<WarningAmberIcon />}
              label="Расходы"
              value={fmtRub(rowTotalExpenses(totals))}
              color={tokens.error}
              bg="rgba(246,193,195,0.4)"
              onClick={() => setDetailCard('expenses')}
            />
            {calcMode === 'net_profit' && (
              <>
                <SummaryCard
                  icon={<InventoryIcon />}
                  label="Затраты (себест.)"
                  value={fmtRub(totals.total_cost)}
                  color={tokens.error}
                  bg="rgba(246,193,195,0.4)"
                />
                <SummaryCard
                  icon={<TrendingUpIcon />}
                  label="Чистая прибыль"
                  value={fmtRub(totals.net_profit)}
                  valueColor={toneColor(totals.net_profit)}
                  color={totals.net_profit >= 0 ? tokens.success : tokens.error}
                  bg={totals.net_profit >= 0 ? 'rgba(191,243,227,0.5)' : 'rgba(246,193,195,0.4)'}
                />
              </>
            )}
          </Box>

          {/* Поиск + управление таблицей */}
          <Box display="flex" alignItems="center" gap={1.5} mb={2} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Поиск по артикулу, бренду, предмету..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: tokens.inkMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300 }}
            />
            <Typography sx={{ fontSize: '0.85rem', color: tokens.inkSoft, flex: 1 }}>
              Найдено: {rows.length} артикулов
            </Typography>
            <Tooltip title="Настройка столбцов">
              <IconButton
                onClick={(e) => setColsAnchor(e.currentTarget)}
                sx={{
                  color: Boolean(colsAnchor) ? tokens.primary : tokens.inkSoft,
                  borderRadius: '10px',
                  border: '1px solid rgba(25,198,192,0.2)',
                  background: Boolean(colsAnchor) ? 'rgba(25,198,192,0.08)' : 'transparent',
                  '&:hover': { background: 'rgba(25,198,192,0.08)', color: tokens.primary },
                }}
              >
                <TuneIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={exportAllowed ? 'Скачать в XLSX (только видимые столбцы)' : EXPORT_LOCKED_TEXT}>
              <IconButton
                onClick={exportAllowed ? downloadXLSX : undefined}
                sx={{
                  color: tokens.inkSoft,
                  borderRadius: '10px',
                  border: '1px solid rgba(25,198,192,0.2)',
                  '&:hover': { background: 'rgba(25,198,192,0.08)', color: tokens.primary },
                }}
              >
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Таблица */}
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: '16px',
              boxShadow: '0 2px 12px rgba(25,198,192,0.08)',
              border: '1px solid rgba(25,198,192,0.08)',
              maxHeight: '70vh',
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {/* Товар */}
                  <SortCell field="sa_name" label="Артикул продавца" align="left" />
                  <SortCell field="nm_id" label="Артикул WB" align="left" />
                  <SortCell field="brand_name" label="Бренд" align="left" />
                  <SortCell field="subject_name" label="Предмет" align="left" />
                  {/* Продажи */}
                  <SortCell field="quantity" label="Продаж" />
                  <SortCell field="return_qty" label="Возвратов" />
                  <SortCell field="net_qty" label="Чистые продажи" />
                  {/* Финансы */}
                  <SortCell field="retail_amount" label="Оплатили покупатели" />
                  <SortCell field="ppvz_sales_commission" label="Комиссия WB" />
                  <SortCell field="acquiring_fee" label="Эквайринг" />
                  <SortCell field="delivery_rub" label="Логистика" />
                  <SortCell field="storage_fee" label="Хранение" />
                  <SortCell field="penalty" label="Штрафы" />
                  <SortCell field="additional_payment" label="Мин. платёж / корр. ВВ" />
                  <SortCell field="ppvz_reward" label="Вознаграждение" />
                  <SortCell field="ppvz_for_pay" label="К перечислению" />
                  <SortCell field="deduction" label="Удержания" />
                  <SortCell field="acceptance" label="Приёмка" />
                  <SortCell field="rebill_logistic_cost" label="Возмещение логист." />
                  <SortCell field="cashback_amount" label="Баллы лояльности" />
                  <SortCell field="cashback_discount" label="Компенс. скидки" />
                  <SortCell field="cashback_commission_change" label="Участие в лояльн." />
                  <SortCell field="payment_schedule" label="Изм. срока оплаты" />
                  <SortCell field="installment_cofinancing_amount" label="Скидка рассрочки" />
                  <SortCell field="effective_payout" label="К выплате" />
                  {/* Чистая прибыль */}
                  {calcMode === 'net_profit' && (
                    <>
                      <SortCell field="cost_per_unit" label="Себест. (ед.)" />
                      <SortCell field="total_cost" label="Затраты" />
                      <SortCell field="net_profit" label="Чистая прибыль" />
                      <SortCell field="margin" label="Маржа %" />
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => {
                  const hasCost = calcMode === 'net_profit' ? Boolean(row.has_cost) : false;
                  const noCost = calcMode === 'net_profit' && !hasCost;
                  const isNegative = calcMode === 'net_profit' && hasCost && row.net_profit < 0;
                  return (
                    <TableRow
                      key={row.nm_id || idx}
                      hover
                      sx={{
                        ...((noCost || isNegative) && {
                          '& td': { background: 'rgba(246,193,195,0.12)' },
                        }),
                      }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: tokens.ink, ...h('sa_name') }}>
                        {row.sa_name || '—'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: tokens.inkSoft, ...h('nm_id') }}>
                        {row.nm_id || '—'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', ...h('brand_name') }}>{row.brand_name || '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', ...h('subject_name') }}>{row.subject_name || '—'}</TableCell>
                      {/* Кол-во */}
                      <TableCell align="right" sx={h('quantity')}>{fmtNum(row.quantity)}</TableCell>
                      <TableCell align="right" sx={{ color: row.return_qty > 0 ? tokens.negative : 'inherit', ...h('return_qty') }}>
                        {fmtNum(row.return_qty)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, ...h('net_qty') }}>
                        {fmtNum(row.net_qty)}
                      </TableCell>
                      {/* Финансы */}
                      <TableCell align="right" sx={h('retail_amount')}>{fmtRub(row.retail_amount)}</TableCell>
                      <TableCell align="right" sx={{ color: toneColor(row.ppvz_sales_commission, 'expense'), ...h('ppvz_sales_commission') }}>
                        {fmtRub(row.ppvz_sales_commission)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: toneColor(row.acquiring_fee, 'expense'), ...h('acquiring_fee') }}>
                        {fmtRub(row.acquiring_fee)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: toneColor(row.delivery_rub, 'expense'), ...h('delivery_rub') }}>
                        {fmtRub(row.delivery_rub)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: toneColor(row.storage_fee, 'expense'), ...h('storage_fee') }}>
                        {fmtRub(row.storage_fee)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: row.penalty > 0 ? tokens.negative : tokens.inkMuted, ...h('penalty') }}>
                        {fmtRub(row.penalty)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: toneColor(row.additional_payment, 'expense'), ...h('additional_payment') }}>
                        {(row.additional_payment || 0) !== 0 ? fmtRub(row.additional_payment) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: (row.ppvz_reward || 0) > 0 ? tokens.negative : tokens.inkMuted, ...h('ppvz_reward') }}>
                        {(row.ppvz_reward || 0) > 0 ? fmtRub(row.ppvz_reward) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: toneColor(row.ppvz_for_pay), whiteSpace: 'nowrap', ...h('ppvz_for_pay') }}>
                        {fmtRub(row.ppvz_for_pay)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: (row.deduction || 0) > 0 ? tokens.negative : tokens.inkMuted, ...h('deduction') }}>
                        {(row.deduction || 0) > 0 ? fmtRub(row.deduction) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: (row.acceptance || 0) > 0 ? tokens.negative : tokens.inkMuted, ...h('acceptance') }}>
                        {(row.acceptance || 0) > 0 ? fmtRub(row.acceptance) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: toneColor(row.rebill_logistic_cost, 'expense'), ...h('rebill_logistic_cost') }}>
                        {(row.rebill_logistic_cost || 0) !== 0 ? fmtRub(row.rebill_logistic_cost) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: toneColor(row.cashback_amount, 'expense'), ...h('cashback_amount') }}>
                        {(row.cashback_amount || 0) !== 0 ? fmtRub(row.cashback_amount) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: toneColor(row.cashback_discount, 'expense'), ...h('cashback_discount') }}>
                        {(row.cashback_discount || 0) !== 0 ? fmtRub(row.cashback_discount) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: toneColor(row.cashback_commission_change, 'expense'), ...h('cashback_commission_change') }}>
                        {(row.cashback_commission_change || 0) !== 0 ? fmtRub(row.cashback_commission_change) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: toneColor(row.payment_schedule, 'expense'), ...h('payment_schedule') }}>
                        {(row.payment_schedule || 0) !== 0 ? fmtRub(row.payment_schedule) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: toneColor(row.installment_cofinancing_amount, 'expense'), ...h('installment_cofinancing_amount') }}>
                        {(row.installment_cofinancing_amount || 0) !== 0 ? fmtRub(row.installment_cofinancing_amount) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: toneColor(row.effective_payout), whiteSpace: 'nowrap', ...h('effective_payout') }}>
                        {row.effective_payout == null ? '—' : fmtRub(row.effective_payout)}
                      </TableCell>
                      {/* Чистая прибыль */}
                      {calcMode === 'net_profit' && (
                        <>
                          {(() => {
                            const isEdited = editedCostIds.has(String(row.nm_id));
                            return (
                              <TableCell
                                align="right"
                                onClick={() => inlineCostId !== row.nm_id && handleCostEditStart(row)}
                                sx={{ cursor: 'text', minWidth: 150, ...h('cost_per_unit') }}
                              >
                                {inlineCostId === row.nm_id ? (
                                  <TextField
                                    autoFocus
                                    size="small"
                                    type="number"
                                    value={inlineCostValue}
                                    onChange={(e) => setInlineCostValue(e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                    onBlur={() => handleCostEditSave(row)}
                                    onKeyDown={(e) => handleCostEditKeyDown(e, row)}
                                    onClick={(e) => e.stopPropagation()}
                                    inputProps={{ min: 0, step: '0.01', style: { width: 90, textAlign: 'right' } }}
                                    sx={{ width: 120 }}
                                  />
                                ) : hasCost ? (
                                  <Box display="inline-flex" alignItems="center" gap={0.5}>
                                    <Chip
                                      label={fmtRub(row.cost_per_unit)}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        cursor: 'text',
                                        fontWeight: 600,
                                        ...(isEdited && {
                                          borderColor: '#F59E0B',
                                          color: '#F59E0B',
                                          background: 'rgba(245,158,11,0.06)',
                                        }),
                                      }}
                                    />
                                    {isEdited && (
                                      <Tooltip title="Задано вручную — нажмите чтобы вернуть исходное значение">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => handleCostReset(e, row)}
                                          sx={{ p: 0.25, color: '#F59E0B', '&:hover': { color: '#D97706' } }}
                                        >
                                          <UndoIcon sx={{ fontSize: '0.95rem' }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                ) : (
                                  <Chip
                                    label="нажмите чтобы задать"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ cursor: 'text', fontSize: '0.7rem' }}
                                  />
                                )}
                              </TableCell>
                            );
                          })()}
                          <TableCell align="right" sx={{ color: hasCost ? toneColor(row.total_cost, 'expense') : tokens.inkMuted, ...h('total_cost') }}>
                            {hasCost ? fmtRub(row.total_cost) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: toneColor(row.net_profit), whiteSpace: 'nowrap', ...h('net_profit') }}>
                            {row.net_profit != null ? fmtRub(row.net_profit) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: row.margin == null ? tokens.inkMuted : row.margin > 20 ? tokens.positive : row.margin > 0 ? tokens.warning : tokens.negative, ...h('margin') }}>
                            {row.margin != null ? `${Number(row.margin).toFixed(1)}%` : '—'}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}

                {/* Итоговая строка */}
                <TableRow
                  sx={{
                    background: 'linear-gradient(90deg, rgba(25,198,192,0.06), rgba(25,198,192,0.02))',
                    '& td': { fontWeight: 700, borderTop: '2px solid rgba(25,198,192,0.2)' },
                  }}
                >
                  <TableCell sx={{ color: tokens.ink, ...h('sa_name') }}>
                    ИТОГО ({rows.length} артикулов)
                  </TableCell>
                  <TableCell sx={h('nm_id')} />
                  <TableCell sx={h('brand_name')} />
                  <TableCell sx={h('subject_name')} />
                  <TableCell align="right" sx={h('quantity')}>{fmtNum(totals.quantity)}</TableCell>
                  <TableCell align="right" sx={{ color: toneColor(totals.return_qty, 'expense'), ...h('return_qty') }}>
                    {fmtNum(totals.return_qty)}
                  </TableCell>
                  <TableCell align="right" sx={h('net_qty')}>{fmtNum(totals.net_qty)}</TableCell>
                  <TableCell align="right" sx={h('retail_amount')}>{fmtRub(totals.retail_amount)}</TableCell>
                  <TableCell align="right" sx={{ color: tokens.inkMuted, ...h('ppvz_sales_commission') }}>—</TableCell>
                  <TableCell align="right" sx={{ color: tokens.inkMuted, ...h('acquiring_fee') }}>—</TableCell>
                  <TableCell align="right" sx={{ color: toneColor(totals.delivery_rub, 'expense'), ...h('delivery_rub') }}>
                    {fmtRub(totals.delivery_rub)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: toneColor(totals.storage_fee, 'expense'), ...h('storage_fee') }}>
                    {fmtRub(totals.storage_fee)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: toneColor(totals.penalty, 'expense'), ...h('penalty') }}>
                    {fmtRub(totals.penalty)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: toneColor(totals.additional_payment, 'expense'), ...h('additional_payment') }}>
                    {totals.additional_payment !== 0 ? fmtRub(totals.additional_payment) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: totals.ppvz_reward > 0 ? tokens.negative : tokens.inkMuted, ...h('ppvz_reward') }}>
                    {totals.ppvz_reward > 0 ? fmtRub(totals.ppvz_reward) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: toneColor(totals.ppvz_for_pay), ...h('ppvz_for_pay') }}>
                    {fmtRub(totals.ppvz_for_pay)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: toneColor(totals.deduction, 'expense'), ...h('deduction') }}>
                    {totals.deduction > 0 ? fmtRub(totals.deduction) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: toneColor(totals.acceptance, 'expense'), ...h('acceptance') }}>
                    {totals.acceptance > 0 ? fmtRub(totals.acceptance) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: toneColor(totals.rebill_logistic_cost, 'expense'), ...h('rebill_logistic_cost') }}>
                    {totals.rebill_logistic_cost !== 0 ? fmtRub(totals.rebill_logistic_cost) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: toneColor(totals.cashback_amount, 'expense'), ...h('cashback_amount') }}>
                    {totals.cashback_amount !== 0 ? fmtRub(totals.cashback_amount) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: toneColor(totals.cashback_discount, 'expense'), ...h('cashback_discount') }}>
                    {totals.cashback_discount !== 0 ? fmtRub(totals.cashback_discount) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: toneColor(totals.cashback_commission_change, 'expense'), ...h('cashback_commission_change') }}>
                    {totals.cashback_commission_change !== 0 ? fmtRub(totals.cashback_commission_change) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: toneColor(totals.payment_schedule, 'expense'), ...h('payment_schedule') }}>
                    {totals.payment_schedule !== 0 ? fmtRub(totals.payment_schedule) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: toneColor(totals.installment_cofinancing_amount, 'expense'), ...h('installment_cofinancing_amount') }}>
                    {totals.installment_cofinancing_amount !== 0 ? fmtRub(totals.installment_cofinancing_amount) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap', ...h('effective_payout') }}>
                    {reportData?.payout
                      ? <Box component="span" sx={tonePill(totals.effective_payout)}>{fmtRub(totals.effective_payout)}</Box>
                      : '—'}
                  </TableCell>
                  {calcMode === 'net_profit' && (
                    <>
                      <TableCell align="right" sx={h('cost_per_unit')}>—</TableCell>
                      <TableCell align="right" sx={{ color: toneColor(totals.total_cost, 'expense'), ...h('total_cost') }}>
                        {fmtRub(totals.total_cost)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', ...h('net_profit') }}>
                        <Box component="span" sx={tonePill(totals.net_profit)}>{fmtRub(totals.net_profit)}</Box>
                      </TableCell>
                      <TableCell align="right" sx={h('margin')}>
                        {totals.retail_amount
                          ? `${((totals.net_profit / totals.retail_amount) * 100).toFixed(1)}%`
                          : '—'}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ── Пустое состояние ── */}
      {!loading && !error && rows.length === 0 && reportData && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: tokens.inkMuted,
          }}
        >
          <CalculateIcon sx={{ fontSize: '3rem', mb: 1, opacity: 0.4 }} />
          <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
            Нет данных для отображения
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', mt: 0.5, maxWidth: 640, mx: 'auto' }}>
            {(() => {
              const cov = reportData?.coverage;
              const fmt = (d) => d.split('-').reverse().join('.');
              const accName = accounts.find((a) => a.id === selectedAccountId)?.name || 'этого кабинета';
              if (search) return 'Поиск не нашёл совпадений — измените запрос.';
              if (!cov?.from) return `Финансовые отчёты кабинета «${accName}» ещё не загружены, поэтому считать пока не из чего. Ход загрузки — на странице «Состояние загрузки».`;
              if (cov.to && period.from > cov.to) return `Финансовые отчёты загружены с ${fmt(cov.from)} по ${fmt(cov.to)} — за выбранный период их ещё нет. WB публикует отчёт за неделю с задержкой в несколько дней.`;
              if (period.to < cov.from) return `Финансовые отчёты загружены с ${fmt(cov.from)}. Более раннюю историю можно запросить на странице «Состояние загрузки».`;
              return `За выбранный период операций нет. Финансовые отчёты загружены с ${fmt(cov.from)}${cov.to ? ` по ${fmt(cov.to)}` : ''}.`;
            })()}
          </Typography>
          {!search && selectedAccountId && (
            <Button component={RouterLink} to={`/marketplaces/sync/${selectedAccountId}`} variant="outlined" size="small" sx={{ mt: 2 }}>
              Состояние загрузки
            </Button>
          )}
        </Box>
      )}

      {!loading && !error && !reportData && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: tokens.inkMuted,
          }}
        >
          <CalculateIcon sx={{ fontSize: '3rem', mb: 1, opacity: 0.3 }} />
          <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
            Выберите кабинет и период для расчёта
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', mt: 0.5 }}>
            Расчёт строится по финансовым отчётам WB, которые MARQIT загружает автоматически
          </Typography>
        </Box>
      )}

      {/* ── Детальный отчёт по плашке ── */}
      <Dialog
        open={Boolean(detailCard)}
        onClose={() => setDetailCard(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px' } }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              {detailCard === 'articles' && 'Все артикулы'}
              {detailCard === 'net_qty' && 'Чистые продажи по артикулам'}
              {detailCard === 'retail_amount' && 'Оплатили покупатели, по артикулам'}
              {detailCard === 'effective_payout' && 'К выплате по артикулам'}
              {detailCard === 'expenses' && 'Структура расходов'}
            </Typography>
            <IconButton onClick={() => setDetailCard(null)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {/* ── Расходы: сводная таблица по категориям + по артикулам ── */}
          {detailCard === 'expenses' && (() => {
            const totalExp = rowTotalExpenses(totals);
            const catRows = EXPENSE_CATEGORIES
              .map((cat) => ({ label: cat.label, amount: totals[cat.key] || 0 }))
              .filter((r) => r.amount > 0)
              .sort((a, b) => b.amount - a.amount);
            const byArticle = [...rows]
              .map((r) => ({ sa_name: r.sa_name, nm_id: r.nm_id, expenses: rowTotalExpenses(r), delivery_rub: r.delivery_rub || 0, storage_fee: r.storage_fee || 0, penalty: r.penalty || 0, deduction: r.deduction || 0, acceptance: r.acceptance || 0 }))
              .filter((r) => r.expenses > 0)
              .sort((a, b) => b.expenses - a.expenses);
            return (
              <Box>
                {/* Категории */}
                <Box sx={{ px: 2.5, py: 1.5, background: 'rgba(246,193,195,0.1)', borderBottom: '1px solid rgba(217,79,92,0.1)' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: tokens.error, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    По статьям расходов
                  </Typography>
                </Box>
                <TableContainer sx={{ maxHeight: 280 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Статья расходов</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Сумма</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Доля</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {catRows.map((r) => (
                        <TableRow key={r.label} hover>
                          <TableCell>{r.label}</TableCell>
                          <TableCell align="right" sx={{ color: tokens.error, fontWeight: 600 }}>{fmtRub(r.amount)}</TableCell>
                          <TableCell align="right" sx={{ color: tokens.inkSoft }}>
                            {totalExp > 0 ? `${((r.amount / totalExp) * 100).toFixed(1)}%` : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid rgba(217,79,92,0.2)' } }}>
                        <TableCell>ИТОГО</TableCell>
                        <TableCell align="right" sx={{ color: tokens.error }}>{fmtRub(totalExp)}</TableCell>
                        <TableCell align="right">100%</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* По артикулам */}
                <Box sx={{ px: 2.5, py: 1.5, background: 'rgba(246,193,195,0.1)', borderTop: '1px solid rgba(217,79,92,0.1)', borderBottom: '1px solid rgba(217,79,92,0.1)' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: tokens.error, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    По артикулам
                  </Typography>
                </Box>
                <TableContainer sx={{ maxHeight: 380 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Артикул продавца</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Артикул WB</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Логистика</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Хранение</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Штрафы</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Удержания</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Приёмка</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: tokens.error }}>Итого расходов</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Доля</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {byArticle.map((r, idx) => (
                        <TableRow key={r.nm_id || idx} hover>
                          <TableCell sx={{ color: tokens.inkMuted, width: 40 }}>{idx + 1}</TableCell>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{r.sa_name || '—'}</TableCell>
                          <TableCell sx={{ color: tokens.inkSoft }}>{r.nm_id || '—'}</TableCell>
                          <TableCell align="right" sx={{ color: tokens.error }}>{r.delivery_rub > 0 ? fmtRub(r.delivery_rub) : '—'}</TableCell>
                          <TableCell align="right" sx={{ color: tokens.error }}>{r.storage_fee > 0 ? fmtRub(r.storage_fee) : '—'}</TableCell>
                          <TableCell align="right" sx={{ color: r.penalty > 0 ? tokens.error : tokens.inkMuted }}>{r.penalty > 0 ? fmtRub(r.penalty) : '—'}</TableCell>
                          <TableCell align="right" sx={{ color: r.deduction > 0 ? tokens.error : tokens.inkMuted }}>{r.deduction > 0 ? fmtRub(r.deduction) : '—'}</TableCell>
                          <TableCell align="right" sx={{ color: r.acceptance > 0 ? tokens.error : tokens.inkMuted }}>{r.acceptance > 0 ? fmtRub(r.acceptance) : '—'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: tokens.error }}>{fmtRub(r.expenses)}</TableCell>
                          <TableCell align="right" sx={{ color: tokens.inkSoft }}>
                            {totalExp > 0 ? `${((r.expenses / totalExp) * 100).toFixed(1)}%` : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })()}

          {/* ── Все артикулы ── */}
          {detailCard === 'articles' && (() => {
            const sorted = [...rows].sort((a, b) => String(a.sa_name || '').localeCompare(String(b.sa_name || '')));
            return (
              <TableContainer sx={{ maxHeight: '70vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Артикул продавца</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Артикул WB</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Бренд</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Чистые продажи</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Оплатили покупатели</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Расходы</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>К выплате</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sorted.map((r, idx) => (
                      <TableRow key={r.nm_id || idx} hover>
                        <TableCell sx={{ color: tokens.inkMuted, width: 40 }}>{idx + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{r.sa_name || '—'}</TableCell>
                        <TableCell sx={{ color: tokens.inkSoft }}>{r.nm_id || '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.brand_name || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtNum(r.net_qty)}</TableCell>
                        <TableCell align="right">{fmtRub(r.retail_amount)}</TableCell>
                        <TableCell align="right" sx={{ color: tokens.error }}>{fmtRub(rowTotalExpenses(r))}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: (r.effective_payout || 0) >= 0 ? tokens.success : tokens.error }}>{r.effective_payout == null ? '—' : fmtRub(r.effective_payout)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()}

          {/* ── Чистые продажи ── */}
          {detailCard === 'net_qty' && (() => {
            const sorted = [...rows].sort((a, b) => (b.net_qty || 0) - (a.net_qty || 0));
            const total = totals.net_qty || 1;
            return (
              <TableContainer sx={{ maxHeight: '70vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Артикул продавца</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Артикул WB</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Заказы</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Возвраты</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: tokens.success }}>Чистые продажи</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Доля</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sorted.map((r, idx) => (
                      <TableRow key={r.nm_id || idx} hover>
                        <TableCell sx={{ color: tokens.inkMuted, width: 40 }}>{idx + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{r.sa_name || '—'}</TableCell>
                        <TableCell sx={{ color: tokens.inkSoft }}>{r.nm_id || '—'}</TableCell>
                        <TableCell align="right">{fmtNum(r.quantity)}</TableCell>
                        <TableCell align="right" sx={{ color: (r.return_qty || 0) > 0 ? tokens.error : 'inherit' }}>{fmtNum(r.return_qty)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: tokens.success }}>{fmtNum(r.net_qty)}</TableCell>
                        <TableCell align="right" sx={{ color: tokens.inkSoft }}>
                          {`${(((r.net_qty || 0) / total) * 100).toFixed(1)}%`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()}

          {/* ── Оплатили покупатели ── */}
          {detailCard === 'retail_amount' && (() => {
            const sorted = [...rows].sort((a, b) => (b.retail_amount || 0) - (a.retail_amount || 0));
            const total = totals.retail_amount || 1;
            return (
              <TableContainer sx={{ maxHeight: '70vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Артикул продавца</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Артикул WB</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Чистые продажи</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: tokens.primary }}>Оплатили покупатели</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Доля</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sorted.map((r, idx) => (
                      <TableRow key={r.nm_id || idx} hover>
                        <TableCell sx={{ color: tokens.inkMuted, width: 40 }}>{idx + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{r.sa_name || '—'}</TableCell>
                        <TableCell sx={{ color: tokens.inkSoft }}>{r.nm_id || '—'}</TableCell>
                        <TableCell align="right">{fmtNum(r.net_qty)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: tokens.primary }}>{fmtRub(r.retail_amount)}</TableCell>
                        <TableCell align="right" sx={{ color: tokens.inkSoft }}>
                          {`${(((r.retail_amount || 0) / total) * 100).toFixed(1)}%`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()}

          {/* ── К выплате ── */}
          {detailCard === 'effective_payout' && (() => {
            const sorted = [...rows].sort((a, b) => (b.effective_payout || 0) - (a.effective_payout || 0));
            const total = totals.effective_payout || 1;
            return (
              <TableContainer sx={{ maxHeight: '70vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Артикул продавца</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Артикул WB</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Оплатили покупатели</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>К перечислению</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: tokens.error }}>Расходы</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: tokens.success }}>К выплате</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Доля</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sorted.map((r, idx) => (
                      <TableRow key={r.nm_id || idx} hover>
                        <TableCell sx={{ color: tokens.inkMuted, width: 40 }}>{idx + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{r.sa_name || '—'}</TableCell>
                        <TableCell sx={{ color: tokens.inkSoft }}>{r.nm_id || '—'}</TableCell>
                        <TableCell align="right">{fmtRub(r.retail_amount)}</TableCell>
                        <TableCell align="right" sx={{ color: tokens.success }}>{fmtRub(r.ppvz_for_pay)}</TableCell>
                        <TableCell align="right" sx={{ color: tokens.error }}>{fmtRub(rowTotalExpenses(r))}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: (r.effective_payout || 0) >= 0 ? tokens.success : tokens.error }}>{r.effective_payout == null ? '—' : fmtRub(r.effective_payout)}</TableCell>
                        <TableCell align="right" sx={{ color: tokens.inkSoft }}>
                          {total !== 0 ? `${(((r.effective_payout || 0) / total) * 100).toFixed(1)}%` : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const CalculatorPage = () => {
  const { marketplace } = useOutletContext();
  return hasData(marketplace) ? <Calculator marketplace={marketplace} /> : <ComingSoon marketplace={marketplace} what="Калькулятор" />;
};

export default CalculatorPage;
